import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { CrewTravelKind, CrewTravelListResponse, CrewTravelRow, CrewTravelStatus } from '../types';
import { kindLabel, statusLabel } from './crewChangesCostPrintMeta';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function dateRange(row: CrewTravelRow): string {
  if (!row.dateFrom && !row.dateTo) return '—';
  if (row.dateFrom && row.dateTo && row.dateFrom !== row.dateTo) {
    return `${fmtDate(row.dateFrom)} – ${fmtDate(row.dateTo)}`;
  }
  return fmtDate(row.dateFrom || row.dateTo);
}

type StatusTone = 'draft' | 'booked' | 'issued' | 'cancelled';
type KindTone = 'crew' | 'visit';

function statusTone(status: CrewTravelStatus): StatusTone {
  switch (status) {
    case 'BOOKED':
      return 'booked';
    case 'ISSUED':
      return 'issued';
    case 'CANCELLED':
      return 'cancelled';
    default:
      return 'draft';
  }
}

function kindTone(kind: CrewTravelKind): KindTone {
  return kind === 'CREW_CHANGE' ? 'crew' : 'visit';
}

function TableHead() {
  return (
    <table className="ccc-print-table ccc-print-table-head">
      <thead>
        <tr>
          <th className="ccc-print-col-no">No.</th>
          <th className="ccc-print-col-type">Type</th>
          <th className="ccc-print-col-status">Status</th>
          <th className="ccc-print-col-vessel">Vessel</th>
          <th className="ccc-print-col-travelers">Travelers</th>
          <th className="ccc-print-col-dates">Dates</th>
          <th className="ccc-print-col-port">Port</th>
          <th className="ccc-print-col-purpose">Purpose</th>
          <th className="ccc-print-col-cost">Cost</th>
          <th className="ccc-print-col-legs">Legs</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({ row, index }: { row: CrewTravelRow; index: number }) {
  const sTone = statusTone(row.status);
  const kTone = kindTone(row.kind);
  return (
    <table className={`ccc-print-table ccc-print-row-table ccc-print-row-${sTone}`}>
      <tbody>
        <tr>
          <td className="ccc-print-col-no">{index + 1}</td>
          <td className="ccc-print-col-type">
            <span className={`ccc-print-pill ccc-print-kind-${kTone}`}>{kindLabel(row.kind)}</span>
          </td>
          <td className="ccc-print-col-status">
            <span className={`ccc-print-pill ccc-print-status-${sTone}`}>
              {statusLabel(row.status)}
            </span>
          </td>
          <td className="ccc-print-col-vessel">{dash(row.vesselAbrv || row.vessel)}</td>
          <td className="ccc-print-col-travelers">{dash(row.passengerSummary)}</td>
          <td className="ccc-print-col-dates">{dateRange(row)}</td>
          <td className="ccc-print-col-port">{dash(row.changePort)}</td>
          <td className="ccc-print-col-purpose">{dash(row.purpose)}</td>
          <td className="ccc-print-col-cost">{dash(row.costSummary)}</td>
          <td className="ccc-print-col-legs">{row.segmentCount}</td>
        </tr>
      </tbody>
    </table>
  );
}

export type CrewChangesCostReportProps = {
  data: CrewTravelListResponse;
  rows: CrewTravelRow[];
  filterLabels: {
    vessel?: string | null;
    kind?: string | null;
    status?: string | null;
    search?: string | null;
  };
};

export const CrewChangesCostReport: React.FC<CrewChangesCostReportProps> = ({
  data,
  rows,
  filterLabels,
}) => {
  const summary = data.summary;

  const firstHeader = useMemo(
    () => (
      <div className="ccc-print-flow-header">
        <div className="ccc-print-accent" aria-hidden />
        <ReportHeader>
          <ReportTitleRow title="Crew Changes Cost" formId="Travel tickets" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={dash(filterLabels.vessel || 'All vessels')} />
              <ReportField label="Type" value={dash(filterLabels.kind || 'All')} />
              <ReportField label="Status" value={dash(filterLabels.status || 'All')} />
              <ReportField label="Search" value={dash(filterLabels.search || '—')} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="Records" value={String(rows.length)} />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
              <ReportField label="Crew change" value={String(summary.crewChange)} />
              <ReportField label="Visit" value={String(summary.visit)} />
            </ReportMetaRow>
          </ReportMeta>
          <div className="ccc-print-kpis">
            <div className="ccc-print-kpi ccc-print-kpi-total">
              <span className="ccc-print-kpi-label">Total</span>
              <span className="ccc-print-kpi-value">{summary.total}</span>
            </div>
            <div className="ccc-print-kpi ccc-print-kpi-draft">
              <span className="ccc-print-kpi-label">Draft</span>
              <span className="ccc-print-kpi-value">{summary.draft}</span>
            </div>
            <div className="ccc-print-kpi ccc-print-kpi-booked">
              <span className="ccc-print-kpi-label">Booked</span>
              <span className="ccc-print-kpi-value">{summary.booked}</span>
            </div>
            <div className="ccc-print-kpi ccc-print-kpi-issued">
              <span className="ccc-print-kpi-label">Issued</span>
              <span className="ccc-print-kpi-value">{summary.issued}</span>
            </div>
            <div className="ccc-print-kpi ccc-print-kpi-crew">
              <span className="ccc-print-kpi-label">Changing crew</span>
              <span className="ccc-print-kpi-value">{summary.crewChange}</span>
            </div>
            <div className="ccc-print-kpi ccc-print-kpi-visit">
              <span className="ccc-print-kpi-label">Visit</span>
              <span className="ccc-print-kpi-value">{summary.visit}</span>
            </div>
          </div>
        </ReportHeader>
        <TableHead />
      </div>
    ),
    [filterLabels, rows.length, summary],
  );

  const continueHeader = useMemo(
    () => (
      <div className="ccc-print-flow-header ccc-print-flow-continue">
        <div className="ccc-print-continue-bar">
          <span className="ccc-print-continue-title">Crew Changes Cost</span>
          <span className="ccc-print-continue-hint">continued</span>
        </div>
        <TableHead />
      </div>
    ),
    [],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>Travel costs as recorded on ticket legs (same currency totals when available).</ReportNote>
      </ReportFooter>
    ),
    [],
  );

  const { items, itemKeys } = useMemo(() => {
    if (rows.length === 0) {
      return {
        items: [
          <table key="empty" className="ccc-print-table ccc-print-row-table">
            <tbody>
              <tr>
                <td colSpan={10} className="ccc-print-empty">
                  No crew changes cost records for these filters.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: rows.map((row, i) => <LineRow key={row.id} row={row} index={i} />),
      itemKeys: rows.map((row) => row.id),
    };
  }, [rows]);

  return (
    <ReportFlowPages
      className="crew-changes-cost-print"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default CrewChangesCostReport;
